import { FlexibleContentCard } from '../flexible-content-card/FlexibleContentCard';
import type { FlexibleCarouselResolved } from './types';
import { Carousel } from '@/components/Carousel';

// `columns` is the number of cards visible at once on desktop, mirroring the
// Flexible Content Grid's field of the same name. Mobile is always one card,
// per AC 1 ("in mobile view all cards are full page width").
const PER_VIEW = { '1': 1, '3': 3 } as const;

const dotLabels = (total: number, perView: number) =>
  Array.from(
    { length: Math.ceil(total / perView) },
    (_, i) => `Zu Seite ${i + 1}`,
  );

export const FlexibleCarouselComponent = ({
  resolved,
}: {
  resolved: FlexibleCarouselResolved;
}) => {
  const cards = resolved.cards ?? [];
  if (cards.length === 0) {
    return null;
  }

  const perView = PER_VIEW[resolved.columns] ?? 3;

  // See `FlexibleContentGridComponent` — the resolved phone pair only reaches
  // the cards when the editor ticked `showInheritedPhoneCta` (SH-428).
  const fallbackPhone = resolved.showInheritedPhoneCta
    ? resolved.phone
    : undefined;
  const fallbackMatelsoPhoneLabel = resolved.showInheritedPhoneCta
    ? resolved.matelsoPhoneLabel
    : undefined;

  // Built once and handed to both instances — they keep their own page state,
  // and only one of the two is ever visible.
  const slides = cards.map((card, i) => (
    <FlexibleContentCard
      key={card.id || i}
      card={card}
      background={resolved.background}
      fallbackPhone={fallbackPhone}
      fallbackMatelsoPhoneLabel={fallbackMatelsoPhoneLabel}
      iconSize={resolved.iconSize}
      padding={resolved.cardPadding}
    />
  ));

  // Controls only appear once the cards actually overflow the viewport (AC 1).
  // `showDots` defaults to `slidesPerView === 1`, so both instances have to
  // pass it explicitly — to switch dots on for the 3-per-view desktop
  // instance, and off on mobile when there is nothing to page through.
  const desktopOverflows = cards.length > perView;
  const mobileOverflows = cards.length > 1;

  return (
    <section className="pt-4">
      {/* Nested blocks get no width signal from SlotRenderer, so the widget
          brings its own page container. */}
      <div>
        {/* `slidesPerView` is static, so desktop and mobile are two instances
            behind CSS visibility — the established pattern, see
            TeachersComponent. */}
        <div className="hidden md:block">
          <Carousel
            slides={slides}
            slidesPerView={perView}
            showArrows={desktopOverflows}
            showDots={desktopOverflows}
            slideClassName="px-3"
            slideAlignment="start"
            prevLabel="Vorherige Karte"
            nextLabel="Nächste Karte"
            dotLabels={dotLabels(cards.length, perView)}
          />
        </div>
        <div className="md:hidden">
          <Carousel
            slides={slides}
            slidesPerView={1}
            showArrows={mobileOverflows}
            showDots={mobileOverflows}
            slideClassName="px-3"
            slideAlignment="start"
            prevLabel="Vorherige Karte"
            nextLabel="Nächste Karte"
            dotLabels={dotLabels(cards.length, 1)}
          />
        </div>
      </div>
    </section>
  );
};
